import React, { useEffect, useState } from 'react';
import { track } from '../../third-party/MixPanel';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useGoogleLogin } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';

import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Stack,
    createTheme,
    ThemeProvider,
    Alert,
    IconButton,
} from '@mui/material';

import FacebookIcon from '@mui/icons-material/Facebook';
import AppleIcon from '@mui/icons-material/Apple';
import GoogleIcon from '@mui/icons-material/Google';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import lumaLogo from '../../assets/luma-logo.png';

const axiosInstance = axios.create({
    baseURL: `${import.meta.env.VITE_API_URL}`,
    headers: {
        'Content-Type': 'application/json',
    },
});

const theme = createTheme({
    palette: {
        primary: {
            main: '#A86E64',
        },
        background: {
            default: '#ECEAE5',
            paper: 'transparent',
        },
    },
});

const Register: React.FC = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        track('signup_started', { method: 'email' });
    }, []);

    const googleRegister = useGoogleLogin({
        onSuccess: async (tokenResponse) => {
            try {
                const response = await axiosInstance.post('/api/auth/google', {
                    access_token: tokenResponse.access_token,
                });
                console.log('Google registration success:', response.data);
                login(response.data.token);
                navigate('/home');
            } catch (err) {
                console.error('Google registration error:', err);
                setError(t('register.errorGoogleRegistration'));
            }
        },
        onError: () => {
            setError(t('register.errorGoogleRegistrationGeneric'));
        },
    });

    const handleAppleRegister = () => {
        console.log('Apple registration not implemented');
    };

    const handleFacebookRegister = () => {
        console.log('Facebook registration not implemented');
    };

    const registerAuth = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !password || !confirmPassword || !firstName) {
            setError(t('register.errorFillFields'));
            return;
        }

        if (password !== confirmPassword) {
            setError(t('register.errorPasswordsMatch'));
            return;
        }

        setError('');

        try {
            const response = await axiosInstance.post('/api/auth/register', {
                email,
                password,
                firstName,
                lastName,
            });
            track('signup_success', { method: 'email' });
            login(response.data.token);
            navigate('/home');
        } catch (err: any) {
            console.log(err);
            setError(err?.response?.data?.message || t('register.errorRegistrationFailed'));
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                <Container maxWidth="xs" sx={{ display: 'flex', alignItems: 'center', pt: 4, pb: 4 }}>
                    <Card sx={{ width: '100%', boxShadow: 0, bgcolor: 'background.paper' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="center" mb={2}>
                                <img
                                    src={lumaLogo}
                                    alt="Luma Logo"
                                    style={{ maxWidth: 80, height: 'auto' }}
                                />
                            </Box>

                            <Typography variant="h5" component="h1" textAlign="center" gutterBottom>
                                {t('register.title')}
                            </Typography>

                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label={t('register.firstNameLabel')}
                                autoComplete="given-name"
                                onChange={(e) => setFirstName(e.target.value)}
                            />

                            <TextField
                                margin="normal"
                                fullWidth
                                label={t('register.lastNameLabel')}
                                autoComplete="family-name"
                                onChange={(e) => setLastName(e.target.value)}
                            />

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label={t('register.emailLabel')}
                                type="email"
                                autoComplete="email"
                                onChange={(e) => setEmail(e.target.value)}
                            />

                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label={t('register.passwordLabel')}
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="new-password"
                                    onChange={(e) => setPassword(e.target.value)}
                                />
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={() => setShowPassword(!showPassword)}
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)'
                                    }}
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </Box>

                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    margin="normal"
                                    required
                                    fullWidth
                                    label={t('register.confirmPasswordLabel')}
                                    type={showConfirmPassword ? "text" : "password"}
                                    autoComplete="new-password"
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                />
                                <IconButton
                                    aria-label="toggle confirm password visibility"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    sx={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)'
                                    }}
                                >
                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </Box>

                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                sx={{ mt: 2, mb: 2 }}
                                onClick={registerAuth}
                            >
                                {t('register.registerButton')}
                            </Button>

                            <Box mt={2} mb={2} textAlign="center">
                                <Typography variant="body2" color="textSecondary" sx={{ display: 'none' }}>
                                    {t('register.orRegisterWith')}
                                </Typography>
                            </Box>

                            <Stack spacing={1}>
                                <Button
                                    sx={{ display: 'none' }}
                                    variant="outlined"
                                    color="inherit"
                                    fullWidth
                                    startIcon={<GoogleIcon />}
                                    onClick={() => googleRegister()}
                                >
                                    {t('register.registerWithGoogle')}
                                </Button>

                                <Button
                                    sx={{ display: 'none' }}
                                    variant="outlined"
                                    color="inherit"
                                    fullWidth
                                    startIcon={<AppleIcon />}
                                    onClick={handleAppleRegister}
                                >
                                    {t('register.registerWithApple')}
                                </Button>

                                <Button
                                    sx={{ display: 'none' }}
                                    variant="outlined"
                                    color="inherit"
                                    fullWidth
                                    startIcon={<FacebookIcon />}
                                    onClick={handleFacebookRegister}
                                >
                                    {t('register.registerWithFacebook')}
                                </Button>
                            </Stack>

                            <Box mt={2} textAlign="center">
                                <Typography variant="body2" color="textSecondary">
                                    {t('register.hasAccount')}{' '}
                                    <Link to="/login" style={{ color: theme.palette.primary.main, textDecoration: 'none' }}>
                                        {t('register.signIn')}
                                    </Link>
                                </Typography>
                            </Box>

                        </CardContent>
                    </Card>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default Register;
